import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type TimelineQuality = 'favorable' | 'neutral' | 'avoid';

export type TimelineFactors = {
  nakshatra?: boolean;
  tarabala?: boolean;
  chandrabala?: boolean;
  tithi?: boolean;
  weekday?: boolean;
  karana?: boolean;
  yoga?: boolean;
  muhurta_yoga?: boolean;
  tithi_group?: boolean;
  nakshatra_category?: boolean;
  nakshatra_category_type?: string;
  pushkara_yoga?: boolean;
  varjyam_free?: boolean;
  amrita_gadiya?: boolean;
  hora?: boolean;
  lagna_sign_favorable?: boolean;
  lagna_clear?: boolean;
  lagna_lord_dignified?: boolean;
  lagna_lord_placed_well?: boolean;
  benefics_in_kendra?: boolean;
  malefics_kendra_clear?: boolean;
  eighth_house_clear?: boolean;
  vedha_clear?: boolean;
  dagdha_tithi_clear?: boolean;
  gandanta_clear?: boolean;
};

export type TimelineSegment = {
  startMin: number;
  endMin: number;
  quality: TimelineQuality;
  choghadiyaLabel?: string;
  label?: string;
  factors?: TimelineFactors;
};

export type TimelineOverlay = {
  startMin: number;
  endMin: number;
  label: string;
  type?: string;
};

export type TimelinePin = {
  atMin: number;
  label: string;
};

export type DayTimelinePayload = {
  sunriseMin: number;
  sunrise?: string;
  sunset?: string;
  segments: TimelineSegment[];
  overlays?: TimelineOverlay[];
  pins?: TimelinePin[];
  tridyusprik?: boolean;
};

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

function formatMinLabel(mins: number) {
  const dayOffset = mins >= 1440 ? 1 : 0;
  const m = ((mins % 1440) + 1440) % 1440;
  const hh24 = Math.floor(m / 60);
  const mm = m % 60;
  const am = hh24 < 12;
  const hh12 = ((hh24 + 11) % 12) + 1;
  const base = `${hh12}:${String(mm).padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
  return dayOffset ? `${base} (next day)` : base;
}

function toClientNowMin() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

type ParsedRange = { startMin: number; endMin: number };

function parseTimeToMin(s: unknown) {
  if (s == null) return null;
  const m = String(s)
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(\(\+1\))?$/);
  if (!m) return null;
  let mins = Number(m[1]) * 60 + Number(m[2]);
  if (m[4]) mins += 1440;
  return mins;
}

function parseTimeRange(s: unknown) {
  if (!s) return null;
  const parts = String(s)
    .trim()
    .split(/\s*(?:-|–|to)\s*/);
  if (parts.length < 2) return null;
  const start = parseTimeToMin(parts[0]);
  const end = parseTimeToMin(parts[1]);
  if (start == null || end == null) return null;
  return { startMin: start, endMin: end } satisfies ParsedRange;
}

function applySunriseAnchor(mins: number, sunriseMin: number) {
  return mins < sunriseMin ? mins + 1440 : mins;
}

function anchorRange(range: ParsedRange, sunriseMin: number) {
  let start = applySunriseAnchor(range.startMin, sunriseMin);
  let end = applySunriseAnchor(range.endMin, sunriseMin);
  if (end <= start) end += 1440;
  return { startMin: start, endMin: end } satisfies ParsedRange;
}

export function parseTimelineFromPanchanga(panchanga: any): DayTimelinePayload | null {
  if (!panchanga || typeof panchanga !== 'object') return null;
  const sunMoonTimes = panchanga?.sun_moon_times && typeof panchanga.sun_moon_times === 'object' ? panchanga.sun_moon_times : {};
  const sunrise = sunMoonTimes?.sunrise;

  const sunriseMatch = String(sunrise ?? '06:00:00')
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  const sunriseMin = sunriseMatch ? Number(sunriseMatch[1]) * 60 + Number(sunriseMatch[2]) : 360;

  const qualityMap: Record<string, TimelineQuality> = {
    Amrit: 'favorable',
    Shubha: 'favorable',
    Laabha: 'favorable',
    Chara: 'neutral',
    Udvega: 'avoid',
    Roga: 'avoid',
    Kaala: 'avoid',
  };

  const auspicious = panchanga?.auspicious_timings && typeof panchanga.auspicious_timings === 'object' ? panchanga.auspicious_timings : {};
  const rawChoghadiya: unknown = auspicious?.gauri_choghadiya;
  const segments: TimelineSegment[] = Array.isArray(rawChoghadiya)
    ? rawChoghadiya
        .map((entry): TimelineSegment | null => {
          if (typeof entry !== 'string') return null;
          const idx = entry.indexOf(':');
          if (idx === -1) return null;
          const choghadiyaLabel = entry.slice(0, idx).trim();
          const timePart = entry.slice(idx + 1).trim();
          const rng = parseTimeRange(timePart);
          if (!rng) return null;
          const anchored = anchorRange(rng, sunriseMin);
          const quality = qualityMap[choghadiyaLabel] ?? 'neutral';
          return { ...anchored, choghadiyaLabel, quality } satisfies TimelineSegment;
        })
        .filter((x): x is TimelineSegment => Boolean(x))
    : [];

  if (segments.length === 0) return null;

  const inauspicious =
    panchanga?.inauspicious_timings && typeof panchanga.inauspicious_timings === 'object' ? panchanga.inauspicious_timings : {};
  const overlays: TimelineOverlay[] = [
    { key: 'rahu_kalam', label: 'Rahu Kala' },
    { key: 'yamaganda', label: 'Yamaghanta Kala' },
    { key: 'gulikai', label: 'Gulika Kala' },
    { key: 'durmuhurtham', label: 'Durmuhurta' },
  ]
    .map(({ key, label }): TimelineOverlay | null => {
      const rng = parseTimeRange((inauspicious as any)?.[key]);
      if (!rng) return null;
      const anchored = anchorRange(rng, sunriseMin);
      return { ...anchored, label } satisfies TimelineOverlay;
    })
    .filter((x): x is TimelineOverlay => Boolean(x));

  const advanced = panchanga?.advanced && typeof panchanga.advanced === 'object' ? panchanga.advanced : {};
  const extraOverlays: TimelineOverlay[] = [
    { key: 'varjyam', label: 'Varjya' },
  ]
    .map(({ key, label }): TimelineOverlay | null => {
      const rng = parseTimeRange((advanced as any)?.[key]);
      if (!rng) return null;
      const anchored = anchorRange(rng, sunriseMin);
      return { ...anchored, label, type: 'inauspicious' } satisfies TimelineOverlay;
    })
    .filter((x): x is TimelineOverlay => Boolean(x));

  // Amrita Gadiya — auspicious overlay, rendered separately as a gold accent bar
  const amritaOverlay: TimelineOverlay | null = (() => {
    const rng = parseTimeRange((advanced as any)?.amrita_gadiya);
    if (!rng) return null;
    const anchored = anchorRange(rng, sunriseMin);
    return { ...anchored, label: 'Amrita Kala', type: 'amrita' };
  })();

  const pins: TimelinePin[] = [
    { key: 'brahma_muhurtha', label: 'Brahma Muhurtha' },
    { key: 'abhijit_muhurta', label: 'Abhijit Muhurta' },
    { key: 'godhuli_muhurtha', label: 'Godhuli Muhurtha' },
  ]
    .map(({ key, label }) => {
      const rng = parseTimeRange((advanced as any)?.[key]);
      if (!rng) return null;
      const anchored = anchorRange(rng, sunriseMin);
      return { atMin: anchored.startMin, label } satisfies TimelinePin;
    })
    .filter((x): x is TimelinePin => Boolean(x));

  return {
    sunriseMin,
    sunrise: sunMoonTimes?.sunrise,
    sunset: sunMoonTimes?.sunset,
    segments,
    overlays: [...overlays, ...extraOverlays, ...(amritaOverlay ? [amritaOverlay] : [])],
    pins,
  };
}

function isInauspiciousOverlay(o: TimelineOverlay) {
  return !o.type || o.type === 'inauspicious';
}

function qualityClasses(q: TimelineQuality, hasInauspiciousOverlap?: boolean) {
  if (q === 'favorable') {
    return hasInauspiciousOverlap
      ? 'bg-lime-400 dark:bg-lime-500'       // light/parrot green — degraded favorable
      : 'bg-emerald-500 dark:bg-emerald-400'; // dark green — pure favorable
  }
  if (q === 'avoid') return 'bg-rose-500 dark:bg-rose-400';
  return 'bg-amber-400 dark:bg-amber-300';
}

// Priority for tooltip display: show rarer/less-known factors first.
// Rahu Kala is already in the legend — users know it; show it last.
const OVERLAY_DISPLAY_PRIORITY: Record<string, number> = {
  'Varjya': 1,
  'Durmuhurta': 2,
  'Gulika Kala': 3,
  'Yamaghanta Kala': 4,
  'Rahu Kala': 5,
};

function qualityLabel(q: TimelineQuality, t: any, overlappingNames?: string[]) {
  if (q === 'favorable' && overlappingNames && overlappingNames.length > 0) {
    const sorted = [...overlappingNames].sort(
      (a, b) => (OVERLAY_DISPLAY_PRIORITY[a] ?? 99) - (OVERLAY_DISPLAY_PRIORITY[b] ?? 99)
    );
    const shown = sorted[0];
    return t('chat.day_quality.caution_mixed', 'Favorable with caution — {{label}} active{{extra}}', {
      label: shown,
      extra: sorted.length > 1 ? ` (+${sorted.length - 1} more)` : '',
    });
  }
  if (q === 'favorable') return t('chat.day_quality.favorable', 'Favorable');
  if (q === 'avoid') return t('chat.day_quality.avoid', 'Avoid');
  return t('chat.day_quality.neutral', 'Neutral');
}

function segmentLabel(s: TimelineSegment) {
  return (s.choghadiyaLabel ?? s.label ?? '').trim() || 'Window';
}

const FACTOR_LABELS: Partial<Record<keyof TimelineFactors, string>> = {
  nakshatra: 'Nakshatra',
  tarabala: 'Tarabala',
  chandrabala: 'Chandrabala',
  tithi: 'Tithi',
  weekday: 'Weekday',
  karana: 'Karana',
  yoga: 'Yoga',
  muhurta_yoga: 'Muhurta Yoga',
  tithi_group: 'Tithi Group',
  nakshatra_category: 'Nakshatra Category',
  pushkara_yoga: 'Pushkara Yoga',
  varjyam_free: 'Varjya',
  amrita_gadiya: 'Amrita Gadiya',
  hora: 'Hora Lord',
  lagna_sign_favorable: 'Rising Sign',
  lagna_clear: 'Ascendant Clear',
  lagna_lord_dignified: 'Lagna Lord Strength',
  lagna_lord_placed_well: 'Lagna Lord Placement',
  benefics_in_kendra: 'Benefics in Kendra',
  malefics_kendra_clear: 'Malefics in Kendra',
  eighth_house_clear: '8th House Clear',
  vedha_clear: 'No Vedha',
  dagdha_tithi_clear: 'Dagdha Tithi',
  gandanta_clear: 'Gandanta-Free',
};

type DayQualityTimelineProps = {
  timeline: DayTimelinePayload;
  timelineDate?: string;
  isToday?: boolean;
  className?: string;
  headerControl?: React.ReactNode;
  onSegmentPick?: (payload: { label: string; startMin: number; endMin: number }) => void;
};

export default function DayQualityTimeline({
  timeline,
  timelineDate,
  isToday = true,
  className,
  headerControl,
  onSegmentPick,
}: DayQualityTimelineProps) {
  const { t } = useTranslation('chat');
  const rangeStart = timeline.sunriseMin;
  const rangeEnd = timeline.sunriseMin + 1440;
  const amritaOverlay = useMemo(() => {
    return (timeline.overlays ?? []).find((o) => o.type === 'amrita') ?? null;
  }, [timeline.overlays]);
  const isAdvanced = useMemo(() => {
    return timeline.segments.some((s) => s.factors?.lagna_sign_favorable !== undefined);
  }, [timeline.segments]);
  const tooltipText = isAdvanced
    ? 'Precisely personalised to your birth chart — computed from 23 classical Muhurta factors including Lagna strength, Vedha obstruction & Dagdha Tithi'
    : 'Personalised to your birth Nakshatra & Moon sign using 8 classical Vedic Panchanga factors';
  const benefitLine = isToday
    ? t('chat.day_quality.benefit_today', 'Your best hours to act today')
    : t('chat.day_quality.benefit_selected', 'Your best hours to act on this day');
  const factorsLine = isAdvanced
    ? t('chat.day_quality.factors_advanced', '23 factors · Personalised to your birth chart')
    : t('chat.day_quality.factors_basic', '8 factors · Personalised to your birth Nakshatra');
  const hasGandanta = useMemo(() => (timeline.overlays ?? []).some((o) => o.type === 'gandanta'), [timeline.overlays]);
  const hasVishaghatika = useMemo(() => (timeline.overlays ?? []).some((o) => o.type === 'vishaghatika'), [timeline.overlays]);
  const hasInauspiciousOverlays = useMemo(() => (timeline.overlays ?? []).some((o) => !o.type || o.type === 'inauspicious'), [timeline.overlays]);

  // For each segment, compute which inauspicious overlays overlap it
  const segmentOverlapMap = useMemo(() => {
    const inauspOverlays = (timeline.overlays ?? []).filter(isInauspiciousOverlay);
    return timeline.segments.map((s) => {
      const overlapping = inauspOverlays.filter(
        (o) => o.startMin < s.endMin && o.endMin > s.startMin
      );
      return overlapping.map((o) => o.label);
    });
  }, [timeline.segments, timeline.overlays]);

  const [nowMin, setNowMin] = useState<number>(() => toClientNowMin());
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);
  const timelineFrameRef = useRef<HTMLDivElement | null>(null);
  const [timelineFrameWidth, setTimelineFrameWidth] = useState(0);
  useEffect(() => {
    if (!isToday) return;
    const id = window.setInterval(() => setNowMin(toClientNowMin()), 60_000);
    return () => window.clearInterval(id);
  }, [isToday]);

  useEffect(() => {
    const node = timelineFrameRef.current;
    if (!node) return;

    const updateWidth = () => {
      setTimelineFrameWidth(node.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(() => {
      updateWidth();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const nowAdjusted = nowMin < rangeStart ? nowMin + 1440 : nowMin;

  const toPercent = (mins: number) => ((mins - rangeStart) / (rangeEnd - rangeStart)) * 100;
  const nowPercent = useMemo(() => toPercent(nowAdjusted), [nowAdjusted, rangeStart, rangeEnd]);

  const activeSegment = useMemo(() => {
    if (activeIdx == null) return null;
    const s = timeline.segments[activeIdx];
    return s ? { idx: activeIdx, segment: s } : null;
  }, [activeIdx, timeline.segments]);

  const activeCenterPct = useMemo(() => {
    if (!activeSegment) return null;
    const s = activeSegment.segment;
    const left = clamp(toPercent(s.startMin), 0, 100);
    const right = clamp(toPercent(s.endMin), 0, 100);
    return clamp((left + right) / 2, 0, 100);
  }, [activeSegment, rangeStart, rangeEnd]);

  const tooltipLayout = useMemo(() => {
    if (activeCenterPct == null || timelineFrameWidth <= 0) return null;

    const sidePadding = 8;
    const availableWidth = Math.max(timelineFrameWidth - sidePadding * 2, 0);
    const tooltipWidth = Math.min(240, availableWidth || 240);
    const centerPx = (activeCenterPct / 100) * timelineFrameWidth;
    const maxLeft = Math.max(sidePadding, timelineFrameWidth - tooltipWidth - sidePadding);
    const leftPx = clamp(centerPx - tooltipWidth / 2, sidePadding, maxLeft);
    const arrowLeftPx = clamp(centerPx - leftPx, 14, Math.max(tooltipWidth - 14, 14));

    return {
      arrowLeftPx,
      leftPx,
      tooltipWidth,
    };
  }, [activeCenterPct, timelineFrameWidth]);

  const nextFavorable = useMemo(() => {
    if (!isToday) return null;
    const segs = timeline.segments.slice().sort((a, b) => a.startMin - b.startMin);
    const current = segs.find((s) => nowAdjusted >= s.startMin && nowAdjusted < s.endMin);
    if (current?.quality === 'favorable') {
      return { kind: 'current' as const, segment: current };
    }
    const next = segs.find((s) => s.quality === 'favorable' && s.endMin > nowAdjusted && s.startMin >= nowAdjusted);
    if (next) return { kind: 'next' as const, segment: next };
    return null;
  }, [isToday, timeline.segments, nowAdjusted]);

  const ticks = useMemo(() => {
    const step = 6 * 60;
    const arr = [rangeStart, rangeStart + step, rangeStart + 2 * step, rangeStart + 3 * step, rangeEnd];
    return arr.map((m) => ({ atMin: m, pct: clamp(toPercent(m), 0, 100), label: formatMinLabel(m) }));
  }, [rangeStart, rangeEnd]);

  const displayDate = useMemo(() => {
    if (!timelineDate || typeof timelineDate !== 'string') return '';
    const dt = new Date(`${timelineDate}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return timelineDate;
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(dt);
  }, [timelineDate]);

  return (
    <div className={className}>
      <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white/90 dark:bg-neutral-900/70 backdrop-blur-sm p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className="text-sm font-semibold text-gray-900 dark:text-white"
              title={tooltipText}
            >
              ✦ {t('chat.day_quality.title', 'Personalized Day Quality')}
            </div>
            <div className="mt-1 text-xs font-medium text-gray-700 dark:text-gray-200">
              {benefitLine}
            </div>
            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {factorsLine}
            </div>
            {!!displayDate && !isToday && (
              <div className="mt-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 break-words">
                {t('chat.day_quality.for_date', 'Your day quality for {{date}}', { date: displayDate })}
              </div>
            )}
            <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
              {timeline.sunrise ? `Sunrise ${timeline.sunrise}` : 'Sunrise anchored'} {timeline.sunset ? `• Sunset ${timeline.sunset}` : ''}
            </div>
          </div>
          {(headerControl || isToday) && (
            <div className="shrink-0 flex flex-col gap-2 sm:items-end">
              {headerControl && <div className="w-full sm:w-auto">{headerControl}</div>}
              {isToday && (
                nextFavorable ? (
                  <div className="text-left sm:text-right">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('chat.day_quality.favorable')}</div>
                    <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                      {nextFavorable.kind === 'current'
                        ? `Now until ${formatMinLabel(nextFavorable.segment.endMin)}`
                        : `${formatMinLabel(nextFavorable.segment.startMin)}–${formatMinLabel(nextFavorable.segment.endMin)}`}
                    </div>
                  </div>
                ) : (
                  <div className="text-left sm:text-right">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('chat.day_quality.favorable')}</div>
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">{t('chat.day_quality.none_remaining_today')}</div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div className="mt-3">
	          {!!timeline.tridyusprik && (
	            <div className="mb-2 inline-flex items-center gap-1 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">
	              {isToday
	                ? 'Tridyusprik: Tithi spans 3 sunrises — avoid new undertakings today'
	                : 'Tridyusprik: Tithi spans 3 sunrises — avoid new undertakings on this day'}
	            </div>
	          )}
          <div ref={timelineFrameRef} className="relative w-full mt-5">
            <div className="relative h-12 w-full rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-neutral-950">
              <div className="absolute inset-0 overflow-hidden rounded-xl">
                {timeline.segments.map((s, idx) => {
                  const left = clamp(toPercent(s.startMin), 0, 100);
                  const right = clamp(toPercent(s.endMin), 0, 100);
                  const width = clamp(right - left, 0, 100);
                  const label = segmentLabel(s);
                  const overlappingNames = segmentOverlapMap[idx] ?? [];
                  const hasInauspiciousOverlap = overlappingNames.length > 0;
                  const qualLabel = qualityLabel(s.quality, t, hasInauspiciousOverlap ? overlappingNames : undefined);
                  const title = `${label}: ${formatMinLabel(s.startMin)}–${formatMinLabel(s.endMin)} · ${qualLabel}`;
                  return (
                    <button
                      key={`${s.startMin}-${s.endMin}-${idx}`}
                      type="button"
                      className={`absolute top-0 h-full ${qualityClasses(s.quality, hasInauspiciousOverlap)} opacity-90 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-0`}
                      style={{ left: `${left}%`, width: `${width}%` }}
                      title={title}
                      onMouseEnter={() => {
                        if (!pinned) setActiveIdx(idx);
                      }}
                      onMouseLeave={() => {
                        if (!pinned) setActiveIdx(null);
                      }}
                      onClick={() => {
                        setActiveIdx(idx);
                        setPinned(true);
                        onSegmentPick?.({ label, startMin: s.startMin, endMin: s.endMin });
                      }}
                    />
                  );
                })}
              </div>

              <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                {(timeline.overlays ?? [])
                  .filter((o) => o.type !== 'vishaghatika' && o.type !== 'amrita')
                  .map((o, idx) => {
                  const left = clamp(toPercent(o.startMin), 0, 100);
                  const right = clamp(toPercent(o.endMin), 0, 100);
                  const width = clamp(right - left, 0, 100);
                  const overlayType = o.type ?? '';
                  const overlayHint =
                    overlayType === 'gandanta'
                      ? 'Sensitive Moon junction — avoid starting anything important'
                      : overlayType === 'inauspicious'
                        ? 'Inauspicious period — avoid important undertakings'
                        : '';
                  const title = `${o.label}: ${formatMinLabel(o.startMin)}–${formatMinLabel(o.endMin)}${overlayHint ? ` — ${overlayHint}` : ''}`;
                  const backgroundImage =
                    overlayType === 'gandanta'
                      ? 'repeating-linear-gradient(-45deg, rgba(180,30,100,0.40) 0px, rgba(180,30,100,0.40) 6px, rgba(0,0,0,0.00) 6px, rgba(0,0,0,0.00) 12px)'
                      : overlayType === 'inauspicious'
                        ? 'repeating-linear-gradient(45deg, rgba(220,38,38,0.28) 0px, rgba(220,38,38,0.28) 6px, rgba(0,0,0,0.00) 6px, rgba(0,0,0,0.00) 12px)'
                        : 'repeating-linear-gradient(135deg, rgba(0,0,0,0.20) 0px, rgba(0,0,0,0.20) 6px, rgba(0,0,0,0.00) 6px, rgba(0,0,0,0.00) 12px)';
                  return (
                    <div
                      key={`${o.startMin}-${o.endMin}-${idx}`}
                      className={`absolute top-0 h-full ${overlayType === 'gandanta' ? 'bg-rose-600/10 dark:bg-rose-400/10' : 'bg-black/10 dark:bg-white/10'}`}
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        backgroundImage,
                      }}
                      title={title}
                    />
                  );
                })}
                {(timeline.overlays ?? [])
                  .filter((o) => o.type === 'vishaghatika')
                  .map((o, idx) => {
                    const left = clamp(toPercent(o.startMin), 0, 100);
                    const right = clamp(toPercent(o.endMin), 0, 100);
                    const width = clamp(right - left, 0, 100);
                    const title = `${o.label}: ${formatMinLabel(o.startMin)}–${formatMinLabel(o.endMin)} — Classical “poisonous” period within this Nakshatra; avoid auspicious acts`;
                    return (
                      <div
                        key={`${o.startMin}-${o.endMin}-${idx}`}
                        className="absolute bottom-0 h-[5px] bg-violet-500 dark:bg-violet-400 opacity-80 z-20"
                        style={{ left: `${left}%`, width: `${width}%` }}
                        title={title}
                      />
                    );
                  })}
                {/* Amrita Kala — gold accent bar at top, only in advanced mode */}
                {isAdvanced && amritaOverlay && (() => {
                  const left = clamp(toPercent(amritaOverlay.startMin), 0, 100);
                  const right = clamp(toPercent(amritaOverlay.endMin), 0, 100);
                  const width = clamp(right - left, 0, 100);
                  // Find the underlying segment quality
                  const underSeg = timeline.segments.find(
                    (s) => s.startMin < amritaOverlay.endMin && s.endMin > amritaOverlay.startMin
                  );
                  const baseQuality = underSeg?.quality ?? 'neutral';
                  // Check if inauspicious overlays also overlap this Amrita window
                  const inauspOverlaps = (timeline.overlays ?? [])
                    .filter(isInauspiciousOverlay)
                    .some((o) => o.startMin < amritaOverlay.endMin && o.endMin > amritaOverlay.startMin);
                  // Context-aware tooltip
                  let hint: string;
                  if (baseQuality === 'favorable' && !inauspOverlaps) {
                    hint = 'Excellent time for new undertakings';
                  } else if (baseQuality === 'favorable' && inauspOverlaps) {
                    hint = 'Auspicious window, but mixed due to active inauspicious period';
                  } else if (baseQuality === 'neutral' && !inauspOverlaps) {
                    hint = 'Auspicious window within a neutral period';
                  } else if (baseQuality === 'avoid') {
                    hint = 'Auspicious window, but unfavorable Choghadiya active — proceed with caution';
                  } else {
                    hint = 'Auspicious window, but mixed results due to other factors';
                  }
                  const title = `✦ ${amritaOverlay.label}: ${formatMinLabel(amritaOverlay.startMin)}–${formatMinLabel(amritaOverlay.endMin)} — ${hint}`;
                  return (
                    <div
                      key="amrita-accent"
                      className="absolute top-0 h-[3px] z-20"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        background: 'linear-gradient(90deg, rgba(234,179,8,0.7), rgba(250,204,21,0.9), rgba(234,179,8,0.7))',
                        boxShadow: '0 0 4px rgba(234,179,8,0.5)',
                      }}
                      title={title}
                    />
                  );
                })()}
              </div>

              <div className="absolute inset-0 pointer-events-none">
                {(timeline.pins ?? []).map((p, idx) => {
                  const left = clamp(toPercent(p.atMin), 0, 100);
                  return (
                    <div key={`${p.atMin}-${idx}`} className="absolute top-0 h-full w-0" style={{ left: `${left}%` }}>
                      <div className="absolute top-0 h-full w-px bg-amber-500/70 dark:bg-amber-400/70" />
                      <div className="absolute -top-6 -translate-x-1/2 rounded bg-amber-600 dark:bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
                        ✦ {p.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {isToday && nowPercent >= 0 && nowPercent <= 100 && (
                <div className="absolute top-0 h-full w-0 pointer-events-none" style={{ left: `${nowPercent}%` }}>
                  <div className="absolute top-0 h-full w-0.5 bg-white z-10 shadow-lg" />
                  <div className="absolute -top-6 -translate-x-1/2 rounded bg-gray-800 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    NOW
                  </div>
                </div>
              )}
            </div>

            {activeSegment && activeCenterPct != null && tooltipLayout && (
              <div className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
                {/* Connector line from bar to tooltip */}
                <div
                  className="absolute w-px bg-gray-300 dark:bg-gray-600"
                  style={{ left: `${activeCenterPct}%`, bottom: '100%', height: '24px' }}
                />
                {/* Tooltip box — positioned well above the bar */}
                <div
                  className="absolute z-30 rounded-xl border border-gray-200 dark:border-white/10 bg-white/95 dark:bg-neutral-900/95 shadow-lg px-3 py-2"
                  style={{ left: `${tooltipLayout.leftPx}px`, bottom: 'calc(100% + 28px)', width: `${tooltipLayout.tooltipWidth}px` }}
                >
                  {/* Small arrow pointing down toward the bar */}
                  <div
                    className="absolute -translate-x-1/2 w-2 h-2 rotate-45 border-b border-r border-gray-200 dark:border-white/10 bg-white/95 dark:bg-neutral-900/95"
                    style={{ bottom: '-5px', left: `${tooltipLayout.arrowLeftPx}px` }}
                  />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                        {segmentLabel(activeSegment.segment)}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-300">
                        {formatMinLabel(activeSegment.segment.startMin)}–{formatMinLabel(activeSegment.segment.endMin)} •{' '}
                        {qualityLabel(activeSegment.segment.quality, t, (segmentOverlapMap[activeSegment.idx] ?? []).length > 0 ? segmentOverlapMap[activeSegment.idx] : undefined)}
                      </div>
                    </div>
                    {pinned && (
                      <button
                        type="button"
                        className="pointer-events-auto shrink-0 rounded-md border border-gray-200 dark:border-white/10 bg-white/80 dark:bg-white/5 px-2 py-1 text-[11px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-white/10"
                        onClick={() => {
                          setPinned(false);
                          setActiveIdx(null);
                        }}
                      >
                        {t('chat.day_quality.close')}
                      </button>
                    )}
                  </div>

                  {activeSegment.segment.factors && (
                    <>
                      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                        {(Object.keys(FACTOR_LABELS) as (keyof TimelineFactors)[])
                          .filter((k) => activeSegment.segment.factors?.[k] !== undefined)
                          .map((k) => {
                            const ok = Boolean(activeSegment.segment.factors?.[k]);
                            return (
                              <div key={k} className="flex items-center gap-1.5 text-[11px] text-gray-700 dark:text-gray-200">
                                <span className="font-semibold">{ok ? '✓' : '✗'}</span>
                                <span className="truncate">{FACTOR_LABELS[k]}</span>
                              </div>
                            );
                          })}
                      </div>
                      {(() => {
                        const cat = (activeSegment.segment.factors?.nakshatra_category_type ?? '').trim();
                        if (!cat) return null;
                        const byCat: Record<string, string[]> = {
                          Gentle: ['marriage', 'friendship', 'art', 'healing'],
                          Fixed: ['construction', 'contracts', 'lasting work'],
                          Swift: ['travel', 'learning', 'medical treatment'],
                          Movable: ['journeys', 'change', 'starting new things'],
                          Mixed: ['general tasks'],
                          Sharp: ['surgery', 'confrontation'],
                          Fierce: ['surgery', 'confrontation'],
                        };
                        const tags = byCat[cat] ?? [];
                        if (!tags.length) return null;
                        const prefix = cat === 'Sharp' || cat === 'Fierce'
                          ? t('chat.day_quality.avoid_for_auspicious_use_for')
                          : t('chat.day_quality.good_for');
                        return (
                          <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-300">
                            <span className="font-semibold text-gray-700 dark:text-gray-200">{t('chat.day_quality.nakshatra_category')}:</span> {cat}{' '}
                            <span className="text-gray-500 dark:text-gray-400">•</span> {prefix} {tags.join(', ')}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-2 flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
            {ticks.map((t) => (
              <div key={t.atMin} style={{ width: '20%', textAlign: 'center' }}>
                {t.label}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-700 dark:text-gray-200">
            <span className="inline-flex h-2.5 w-2.5 rounded-sm bg-emerald-500 dark:bg-emerald-400" />
            <span>{t('chat.day_quality.favorable')}</span>
            {segmentOverlapMap.some((names, i) => names.length > 0 && timeline.segments[i]?.quality === 'favorable') && (
              <>
                <span className="inline-flex h-2.5 w-2.5 rounded-sm bg-lime-400 dark:bg-lime-500 ml-2" />
                <span>{t('chat.day_quality.caution')}</span>
              </>
            )}
            <span className="inline-flex h-2.5 w-2.5 rounded-sm bg-amber-400 dark:bg-amber-300 ml-2" />
            <span>{t('chat.day_quality.neutral')}</span>
            <span className="inline-flex h-2.5 w-2.5 rounded-sm bg-rose-500 dark:bg-rose-400 ml-2" />
            <span>{t('chat.day_quality.avoid')}</span>
            {hasInauspiciousOverlays && (
              <>
                <span
                  className="inline-flex h-2.5 w-2.5 rounded-sm ml-2 border border-red-400/40"
                  style={{
                    background: 'repeating-linear-gradient(45deg, rgba(220,38,38,0.28) 0px, rgba(220,38,38,0.28) 3px, transparent 3px, transparent 6px)',
                  }}
                />
                <span>Rahu Kala etc.</span>
              </>
            )}
            {isAdvanced && hasGandanta && (
              <>
                <span className="inline-flex h-2.5 w-2.5 rounded-sm bg-rose-600/60 dark:bg-rose-400/60 ml-2" />
                <span>Gandanta</span>
              </>
            )}
            {isAdvanced && hasVishaghatika && (
              <>
                <span className="inline-flex h-2.5 w-2.5 rounded-sm bg-violet-500/70 dark:bg-violet-400/70 ml-2" />
                <span>Vishaghatika</span>
              </>
            )}
            {isAdvanced && amritaOverlay && (
              <>
                <span
                  className="inline-flex h-2.5 w-2.5 rounded-sm ml-2"
                  style={{ background: 'linear-gradient(90deg, rgba(234,179,8,0.7), rgba(250,204,21,0.9))' }}
                />
                <span>Amrita Kala</span>
              </>
            )}
          </div>
          {isToday && <div className="text-[11px] text-gray-500 dark:text-gray-400">{formatMinLabel(nowAdjusted)}</div>}
        </div>
      </div>
    </div>
  );
}
